import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Form, Modal, Spinner,
} from 'react-bootstrap';
import Error from '../Error';
import KnowledgeBaseUploader from './KnowledgeBaseUploader';
import KnowledgeBaseDocuments from './KnowledgeBaseDocuments';
import KnowledgeBaseService, { AUDIENCES, AUDIENCE_LABELS } from '../../Services/KnowledgeBaseService';
import { KIND_LABELS } from '../CounterpartyPicker';

/**
 * One counterparty's documents, opened from their row.
 *
 * The same satellite-modal shape as Drivers / Fleet / Portal users on the
 * operators screen and Payments on the partners one: launched from the table,
 * handed the row it was opened with, unmounted when hidden.
 *
 * What it adds over the admin screen is that the scope is already settled —
 * the row IS the counterparty — so the scope picker and the counterparty
 * picker are both gone. It is the same uploader and the same document list
 * underneath, handed `fixedScope` / `fixedCounterpartyId`.
 *
 * The knowledge base still has to be chosen, because a counterparty's
 * paperwork could belong in more than one corpus. The default is the first
 * INTERNAL one: a supplier's contract is not customer-safe, and defaulting to
 * a customer-facing corpus would be the wrong mistake to make quietly.
 */

function CounterpartyDocumentsModal({
  show = false,
  handleClose,
  /** 'operator' | 'partner' */
  kind,
  /** The row. Read for its id and its name, nothing else. */
  counterparty = null,
  canManage = false,
}) {
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [kbId, setKbId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  // Operator records spell the id `Id`; partner records use `id`. Both
  // spellings are what the rest of the app already reads.
  const counterpartyId = useMemo(
    () => String(counterparty?.Id || counterparty?.id || ''),
    [counterparty],
  );
  const counterpartyName = counterparty?.name || counterparty?.Name || counterpartyId;
  const kindLabel = KIND_LABELS[kind] || 'Counterparty';

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    KnowledgeBaseService.list()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setKnowledgeBases(list);
        const internal = list.find((kb) => kb.audience === AUDIENCES.INTERNAL);
        setKbId((internal || list[0])?.id || '');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.message || err.message || 'Failed to load knowledge bases');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [show]);

  const selectedKb = knowledgeBases.find((kb) => kb.id === kbId) || null;

  return (
    <Modal show={show} onHide={handleClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-folder-open me-2 text-primary" aria-hidden="true" />
          Documents
          {' — '}
          {counterpartyName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />

        <p className="text-muted small">
          {`Uploaded here, these documents answer questions about this ${kindLabel.toLowerCase()} specifically. `}
          A question asked about them also retrieves the general documents, so
          &quot;your contract says&quot; and &quot;our standard policy is&quot;
          both reach the answer.
        </p>

        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" size="sm" />
            <span className="text-muted ms-2">Loading knowledge bases…</span>
          </div>
        )}

        {!loading && knowledgeBases.length === 0 && (
          <Alert variant="warning" className="mb-0">
            There are no knowledge bases yet. Create one on the Knowledge Base
            screen before uploading documents.
          </Alert>
        )}

        {!loading && knowledgeBases.length > 0 && (
          <>
            <Form.Group className="mb-3" controlId="cp-docs-kb">
              <Form.Label>Knowledge base</Form.Label>
              <Form.Select value={kbId} onChange={(e) => setKbId(e.target.value)}>
                {knowledgeBases.map((kb) => (
                  <option key={kb.id} value={kb.id}>
                    {`${kb.name} (${AUDIENCE_LABELS[kb.audience] || kb.audience})`}
                  </option>
                ))}
              </Form.Select>
              {selectedKb?.audience === AUDIENCES.CUSTOMER_SAFE && (
                <Form.Text className="text-warning">
                  <i className="fa fa-triangle-exclamation me-1" aria-hidden="true" />
                  This knowledge base is customer-safe — anything uploaded here
                  may be quoted back to a customer.
                </Form.Text>
              )}
            </Form.Group>

            {/* Upload is a managing action, so it is behind the permission.
                Reading the list is not. */}
            {canManage ? (
              <div className="mb-4">
                <KnowledgeBaseUploader
                  knowledgeBases={knowledgeBases}
                  kbId={kbId}
                  fixedScope={kind}
                  fixedCounterparty={{ id: counterpartyId, name: counterpartyName }}
                  onUploaded={() => setReloadToken((n) => n + 1)}
                />
              </div>
            ) : (
              <Alert variant="light" className="border py-2 px-3 small">
                <i className="fa fa-lock me-2 text-muted" aria-hidden="true" />
                You can see what is on file here. Uploading and deleting need
                the Knowledge Base permission.
              </Alert>
            )}

            <hr />

            <h6 className="mb-3">
              {`On file for this ${kindLabel.toLowerCase()}`}
            </h6>
            <KnowledgeBaseDocuments
              kbId={kbId}
              fixedScope={kind}
              fixedCounterpartyId={counterpartyId}
              canManage={canManage}
              reloadToken={reloadToken}
              emptyMessage={`No documents on file for this ${kindLabel.toLowerCase()} yet.`}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CounterpartyDocumentsModal;
